import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick help options for Day Off List page
 * Provides guided tours for common actions
 */
export const useDayOffListQuickHelp = (pageType: 'member' | 'hrm') => {
    const isHRM = pageType === 'hrm'

    const options: QuickHelpOption[] = useMemo(() => {
        const memberOptions: QuickHelpOption[] = [
            {
                id: 'dayoff-create',
                label: 'Tạo đơn nghỉ phép',
                description: 'Hướng dẫn tạo đơn nghỉ phép mới',
                selector: '[data-tour="create-dayoff-btn"]',
                tourTitle: 'Tạo đơn nghỉ phép',
                tourDescription:
                    'Nhấn nút này để tạo đơn nghỉ phép mới.\n\n• Chọn loại nghỉ phép (phép năm, ốm đau, việc riêng)\n• Chọn khoảng thời gian nghỉ\n• Nhập lý do nghỉ\n• Gửi đơn chờ phê duyệt\n\nĐơn sẽ được gửi đến trưởng phòng/HRM để xét duyệt.',
            },
            {
                id: 'dayoff-edit',
                label: 'Chỉnh sửa đơn nghỉ',
                description: 'Cập nhật thông tin đơn đang chờ',
                selector: '[data-tour="dayoff-data-table"]',
                tourTitle: 'Chỉnh sửa đơn nghỉ phép',
                tourDescription:
                    'Để chỉnh sửa đơn nghỉ phép:\n\n1. Tìm đơn có trạng thái "Đang chờ"\n2. Click vào icon hành động (⋮) ở cuối hàng\n3. Chọn "Chỉnh sửa"\n4. Cập nhật thông tin cần thay đổi\n5. Lưu lại\n\n**Lưu ý:** Chỉ chỉnh sửa được đơn "Đang chờ" duyệt.',
                demo: 'dayoff-edit-action',
            },
            {
                id: 'dayoff-cancel',
                label: 'Huỷ đơn nghỉ phép',
                description: 'Huỷ đơn đang chờ duyệt',
                selector: '[data-tour="dayoff-data-table"]',
                tourTitle: 'Huỷ đơn nghỉ phép',
                tourDescription:
                    'Để huỷ đơn nghỉ phép:\n\n1. Tìm đơn có trạng thái "Đang chờ"\n2. Click vào icon hành động (⋮) ở cuối hàng\n3. Chọn "Huỷ"\n4. Xác nhận huỷ đơn\n\n**Lưu ý:**\n• Chỉ huỷ được đơn "Đang chờ" duyệt\n• Đơn đã huỷ không thể khôi phục',
                demo: 'dayoff-cancel-action',
            },
            {
                id: 'dayoff-view-stats',
                label: 'Xem số ngày phép',
                description: 'Kiểm tra số ngày phép còn lại',
                tourTitle: 'Thống kê phép năm',
                tourDescription:
                    'Xem tổng quan phép năm của bạn:\n\n• Tổng số ngày phép trong năm\n• Số ngày phép đã dùng\n• Số ngày phép còn lại\n• Số đơn đang chờ duyệt\n\nThống kê được cập nhật theo thời gian thực.',
            },
        ]

        const hrmOptions: QuickHelpOption[] = [
            {
                id: 'dayoff-approve',
                label: 'Duyệt đơn nghỉ phép',
                description: 'Phê duyệt hoặc từ chối đơn',
                tourTitle: 'Duyệt đơn nghỉ phép',
                tourDescription:
                    'Để duyệt đơn nghỉ phép:\n\n1. Click vào hàng đơn cần duyệt\n2. Xem chi tiết thông tin\n3. Click "Duyệt" hoặc "Từ chối"\n4. Nhập ghi chú (nếu cần)\n5. Xác nhận\n\nNhân viên sẽ nhận thông báo ngay.',
            },
            {
                id: 'dayoff-bulk-approve',
                label: 'Duyệt nhiều đơn',
                description: 'Phê duyệt hàng loạt',
                selector: '[data-tour="dayoff-data-table"]',
                tourTitle: 'Duyệt nhiều đơn cùng lúc',
                tourDescription:
                    'Quy trình duyệt nhiều đơn nghỉ phép:\n\n**Bước 1: Chọn phiếu**\nTick checkbox các phiếu cần duyệt. Chỉ phiếu "Đang chờ" mới chọn được.\n\n**Bước 2: Bấm duyệt nhanh**\nThanh Selection hiện ra. Click "Duyệt nhanh".\n\n**Bước 3: Xác nhận**\nModal hiện danh sách. Nhập phản hồi và xác nhận.',
                tourSteps: [
                    {
                        selector: '[data-tour="dayoff-data-table"]',
                        title: 'Bước 1: Chọn phiếu nghỉ',
                        content:
                            'Tick vào checkbox đầu hàng để chọn các phiếu cần duyệt. Có thể chọn nhiều phiếu cùng lúc. Chỉ phiếu "Đang chờ" mới có thể được chọn.',
                        demo: 'select-dayoff-rows',
                    },
                    {
                        selector: '[data-tour="dayoff-data-table"]',
                        title: 'Bước 2: Bấm duyệt nhanh',
                        content:
                            'Sau khi chọn phiếu, thanh Selection sẽ xuất hiện. Click vào nút "Duyệt nhanh" màu trắng để tiếp tục.',
                        demo: 'click-bulk-approve-button',
                    },
                    {
                        selector: '[data-tour="dayoff-data-table"]',
                        title: 'Bước 3: Xem và xác nhận',
                        content:
                            'Modal hiện ra với danh sách đơn đã chọn. Kiểm tra thông tin, nhập phản hồi cho Leader/BOD, rồi click "Xác nhận" để hoàn tất.',
                        demo: 'bulk-approve-dayoff-modal',
                    },
                ],
            },
            {
                id: 'dayoff-view-stats-hrm',
                label: 'Xem thống kê',
                description: 'Tổng quan đơn nghỉ phép',
                selector: '[data-tour="dayoff-stats"]',
                tourTitle: 'Thống kê HRM',
                tourDescription:
                    'Xem tổng quan toàn bộ đơn nghỉ phép:\n\n• Tổng số đơn\n• Số đơn chờ duyệt\n• Số đơn đã duyệt\n• Số đơn từ chối\n• Tổng số ngày nghỉ\n\nThống kê theo bộ lọc hiện tại.',
            },
            {
                id: 'dayoff-export',
                label: 'Xuất Excel',
                description: 'Export danh sách ra file',
                selector: '[data-tour="dayoff-settings"]',
                tourTitle: 'Export danh sách',
                tourDescription:
                    'Xuất danh sách đơn nghỉ phép:\n\n• Click icon Settings (Settings)\n• Chọn "Export Excel"\n• File xuất theo bộ lọc hiện tại\n• Chứa đầy đủ thông tin đơn\n\nHữu ích cho báo cáo định kỳ.',
            },
        ]

        const commonOptions: QuickHelpOption[] = [
            {
                id: 'dayoff-search',
                label: 'Tìm kiếm đơn',
                description: 'Tìm nhanh theo nhiều tiêu chí',
                selector: '[data-tour="dayoff-search"]',
                tourTitle: 'Tìm kiếm đơn nghỉ phép',
                tourDescription:
                    'Tìm kiếm nhanh đơn nghỉ phép:\n\n• Tên nhân viên\n• Phòng ban\n• Loại nghỉ phép\n• Lý do nghỉ\n\nKết quả hiển thị ngay khi gõ.',
            },
            {
                id: 'dayoff-filter',
                label: 'Lọc danh sách',
                description: 'Sử dụng bộ lọc nâng cao',
                selector: '[data-tour="dayoff-filter-bar"]',
                tourTitle: 'Bộ lọc đơn nghỉ phép',
                tourDescription:
                    'Lọc đơn nghỉ phép theo:\n\n• Trạng thái (chờ duyệt, đã duyệt, từ chối, đã hủy)\n• Loại nghỉ phép (phép năm, ốm đau, việc riêng)\n• Khoảng thời gian\n• Phòng ban (HRM)\n• Nhân viên (HRM)\n\nKết hợp nhiều bộ lọc để tìm chính xác.',
            },
            {
                id: 'dayoff-expand-details',
                label: 'Xem chi tiết thời gian',
                description: 'Mở rộng xem từng ngày nghỉ',
                selector: '[data-tour="dayoff-data-table"]',
                tourTitle: 'Chi tiết thời gian nghỉ',
                tourDescription:
                    'Xem chi tiết từng ngày nghỉ:\n\n• Click icon mũi tên (›) ở đầu hàng\n• Xem danh sách ngày nghỉ cụ thể\n• Thấy loại nghỉ từng ngày (cả ngày/nửa ngày)\n• Click lại để thu gọn\n\nHữu ích cho đơn nghỉ nhiều ngày.',
            },
            {
                id: 'dayoff-table-settings',
                label: 'Tuỳ chỉnh bảng',
                description: 'Cài đặt hiển thị bảng',
                selector: '[data-tour="dayoff-settings"]',
                tourTitle: 'Cài đặt bảng',
                tourDescription: isHRM
                    ? 'Click icon Settings (Settings) để:\n\n• Điều chỉnh cỡ chữ\n• Thay đổi chiều cao hàng\n• Ẩn/hiện cột\n• Export Excel\n• Reset về mặc định\n\nCài đặt được lưu tự động.'
                    : 'Click icon Settings (Settings) để:\n\n• Điều chỉnh cỡ chữ\n• Thay đổi chiều cao hàng\n• Ẩn/hiện cột\n• Reset về mặc định\n\nCài đặt được lưu tự động.',
            },
            {
                id: 'dayoff-overview-tour',
                label: 'Xem tour tổng quan',
                description: 'Hướng dẫn sử dụng trang',
                tourTitle: 'Tour hướng dẫn',
                tourDescription:
                    'Nhấn nút "Hướng dẫn sử dụng" ở góc phải màn hình để xem tour chi tiết các tính năng quản lý nghỉ phép.',
            },
        ]

        return isHRM
            ? [...hrmOptions, ...commonOptions]
            : [...memberOptions, ...commonOptions]
    }, [isHRM])

    useQuickHelp(options)

    return { options }
}

export default useDayOffListQuickHelp
